// src/backendServices/debugManagment/remoteLogger.ts
//
// Système de logs unifié :
//  • Intercepte console.* globalement dès l'installation (index.tsx)
//  • Déduplique les messages répétitifs (compteur, comme le navigateur)
//  • Expose createLogger() pour les logs métier ciblés
//  • Gère deux événements enrichis (IP + UA + AppInfo) : INIT et LOGIN
//  • Le module (= nom de fichier Symfony) évolue automatiquement après le login

import axios from 'axios';
import { API_URLS } from '../urls';
import { getUserAgentInformations } from '../../utils/utils_userAgent';
import { getAppInformations       } from '../../utils/utils_app';

// ─── Configuration ────────────────────────────────────────────────────────────

export type LogLevel = 'log' | 'info' | 'warn' | 'error' | 'debug';

const REMOTE_LOG_ENABLED  = true;
const CONSOLE_LOG_ENABLED = true;
// Autorise le canal de métriques (relevés de débit, qualité réseau) — voir la
// section « Canal de métriques » en bas de ce fichier. C'est l'interrupteur du
// développeur : celui de l'opérateur est le bouton de l'onglet Débits.
const METRICS_LOG_ENABLED = true;

/**
 * Sondes de diagnostic de la régie : [AV-SYNC] (lecture de chaque image vidéo +
 * minuteur de 10 ms par entrée audio), [ASR-PROBE], [RESSOURCES], [AUDIO-CLOCK],
 * [HLS-CLOCK], [AV-SENDER]. Elles servent aux enquêtes, pas à l'antenne, et
 * [AV-SYNC] à lui seul coûte plusieurs points du fil principal. À false par
 * défaut ; passer à true et rebuilder pour une enquête.
 * Hors périmètre : [PROBE régie] (presque gratuite, détecte les blocages du fil
 * principal), [WHIP-ENC], [DÉBITS] (canal de métriques, interrupteur opérateur).
 */
export const DIAGNOSTIC_PROBES_ENABLED = false;

// Niveaux réellement envoyés au serveur.
//
// Restreint à warn/error pendant le diagnostic du figement de la régie, et ce
// n'est pas de la frilosité : `flushQueue` envoie UNE requête HTTP par entrée,
// séquentiellement, toutes les 3 s. Le chemin de connexion d'un device compte à
// lui seul une centaine de `console.log` distincts — que la déduplication ne
// regroupe pas, puisqu'ils diffèrent tous. Tout envoyer ajouterait une centaine
// de requêtes à un thread principal dont on cherche justement à savoir pourquoi
// il sature : la mesure changerait ce qu'elle mesure.
//
// Tous les logs restent visibles en console (CONSOLE_LOG_ENABLED), et toute
// l'instrumentation du figement — sonde de thread, fermetures de PC, fin de
// piste, chute de connexion — est en warn ou en error, donc couverte.
const GLOBAL_REMOTE_LEVELS: LogLevel[] = ['warn', 'error'];

// Délai du batch pour les logs globaux (ms)
const BATCH_INTERVAL_MS = 3000;

// Fenêtre de déduplication : si le même message revient dans ce délai (ms),
// on incrémente un compteur au lieu d'empiler une nouvelle entrée.
// Exemple : "VU-mètre frame X" toutes les 16ms → une seule entrée "[x180] VU-mètre frame…"
const DEDUP_WINDOW_MS = 3000;

// Messages à ignorer — libs tierces bruyantes qui ne nous apportent rien
const GLOBAL_IGNORE_PATTERNS: RegExp[] = [
  /React Router Future Flag Warning/,
  /react-dom/,
  /Warning: Each child in a list/,
  /Warning: Can't perform a React state update/,
  /\[webpack/,
  /\[HMR\]/,
  /\[WDS\]/,
  /Download the React DevTools/,
];

// ─── État interne partagé ─────────────────────────────────────────────────────

const originals: Record<LogLevel, (...args: unknown[]) => void> = {
  log   : console.log.bind(console),
  info  : console.info.bind(console),
  warn  : console.warn.bind(console),
  error : console.error.bind(console),
  debug : console.debug.bind(console),
};

// Identité courante : évolue en deux étapes
//   1. À l'installation  → app_<platform>_unknown
//   2. Après login       → app_<platform>_<userId>
let currentModule = 'app_admin_unknown';

// Plateforme retenue à l'installation, pour que `setLoggerIdentity` n'ait pas à
// la redemander : l'appelant qui connaît l'identité ne connaît pas forcément la
// plateforme, et la lui faire redétecter, c'est risquer deux réponses
// différentes dans le même fichier.
let currentPlatform = 'unknown';

export interface LoggerIdentity {
  /** D'où vient le journal : la console d'administration, ou un téléphone. */
  app     : 'admin' | 'remote_device';
  /** La chaîne sur laquelle on travaille — son nom quand on l'a, son UUID sinon. */
  channel : string;
  /** Le poste tenu : `Regie`, `Cam_P2P`, `Cam_SFU`. */
  poste   : string;
  /** Qui : l'adresse de l'administrateur, le nom d'affichage du téléphone. */
  login   : string;
}

// Les morceaux connus à cet instant. Ils n'arrivent PAS ensemble et c'est le
// coeur du problème : la plateforme est connue au démarrage, l'identité au
// login, la chaîne quand la régie en ouvre une, et le transport seulement quand
// la régie a répondu. Chaque appelant dépose ce qu'il sait, le nom se recompose.
const identity: LoggerIdentity = {
  app: 'admin', channel: '', poste: '', login: '',
};

// Symfony valide le nom de fichier sur `^[\w\-. ]+\.txt$` et écrit en SILENCE
// hors de là — on ne s'en aperçoit qu'en cherchant un fichier qui n'existe pas.
// Or ces morceaux sont des saisies libres : une adresse e-mail porte un `@`, un
// nom de chaîne des accents et des espaces. Chacun est donc assaini, et borné :
// mis bout à bout, quatre champs libres feraient un nom de fichier hors limites.
const propre = (v: string, max: number): string =>
  // Les accents sont TRANSLITTÉRÉS, pas supprimés : sans ça « Caméra plateau »
  // devenait « Cam-ra-plateau », qui ne se lit plus et ne se cherche plus.
  v.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
   .replace(/[^\w\-.]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '').slice(0, max);

/**
 * Recompose le nom du fichier :
 *
 *     app_{admin|remote_device}_{OS}_Channel_{chaîne}_{poste}_{login}
 *
 * Symfony y ajoutera `remoteLog_{IP}_` devant. Les champs encore inconnus sont
 * OMIS plutôt que remplis d'un `unknown` : un fichier nommé
 * `app_admin_Desktop_alex-lovelight.tv` dit déjà ce qu'il sait, et le nom
 * s'allonge à mesure que la session se précise. Un `unknown` en position fixe
 * ferait croire à une information manquante alors qu'elle n'existe pas encore.
 */
const composeModule = (): string => {
  const morceaux = ['app', identity.app, currentPlatform];
  if (identity.channel) morceaux.push('Channel', propre(identity.channel, 28));
  if (identity.poste)   morceaux.push(propre(identity.poste, 12));
  if (identity.login)   morceaux.push(propre(identity.login, 40));
  return morceaux.join('_');
};


// ─── Queue avec déduplication ─────────────────────────────────────────────────

interface QueueEntry {
  level   : LogLevel;
  message : string; // message brut (sans le préfixe [xN])
  ts      : string; // timestamp de la PREMIÈRE occurrence
  url     : string;
  count   : number; // nombre d'occurrences dans la fenêtre DEDUP_WINDOW_MS
  lastSeen: number; // timestamp ms de la dernière occurrence (pour la fenêtre)
}

// Clé de déduplication = level + message (insensible au timestamp)
const dedupKey = (level: LogLevel, message: string) => `${level}::${message}`;

let queue      : QueueEntry[]                           = [];
let dedupMap   : Map<string, QueueEntry>                = new Map();
let batchTimer : ReturnType<typeof setInterval> | null  = null;
let isInstalled = false;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getTime = (): string =>
  new Date().toLocaleTimeString('fr-FR', {
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    fractionalSecondDigits: 3,
  });

const serializeArgs = (args: unknown[]): string =>
  args.map(a => {
    if (a instanceof Error) return `${a.name}: ${a.message}`;
    if (typeof a === 'object') { try { return JSON.stringify(a); } catch { return String(a); } }
    return String(a);
  }).join(' ');

const isIgnored = (message: string): boolean =>
  GLOBAL_IGNORE_PATTERNS.some(re => re.test(message));

// Formate le message final pour l'envoi (ajoute [xN] si répété)
const formatMessage = (entry: QueueEntry): string =>
  entry.count > 1
    ? `[x${entry.count}] ${entry.message}`
    : entry.message;

// ─── Ajout dans la queue avec déduplication ───────────────────────────────────

const enqueue = (level: LogLevel, message: string): void => {
  const now = Date.now();
  const key = dedupKey(level, message);
  const existing = dedupMap.get(key);

  if (existing && (now - existing.lastSeen) < DEDUP_WINDOW_MS) {
    // Même message dans la fenêtre → on incrémente, pas de nouvelle entrée
    existing.count++;
    existing.lastSeen = now;
    return;
  }

  // Nouveau message ou fenêtre expirée → nouvelle entrée
  const entry: QueueEntry = {
    level,
    message,
    ts      : new Date().toISOString(),
    url     : window.location.href,
    count   : 1,
    lastSeen: now,
  };
  queue.push(entry);
  dedupMap.set(key, entry);
};

// ─── Envoi distant ────────────────────────────────────────────────────────────
// connection = true  → Symfony enrichit le log avec IP + UA + AppInfo
// connection = false → log simple, headers UA/AppInfo envoyés quand même

const sendRemoteLog = async (
  module     : string,
  level      : LogLevel,
  message    : string,
  connection : boolean = false,
): Promise<void> => {
  if (!REMOTE_LOG_ENABLED) return;
  try {
    await axios.post(
      API_URLS.remoteLog,
      { message: `[${level.toUpperCase()}] ${message}`, filename: `${module}.txt`, connection },
      {
        headers: {
          'Accept'                  : 'application/ld+json',
          'User-Agent-Informations' : String(getUserAgentInformations()),
          'App-Informations'        : String(getAppInformations()),
        },
      },
    );
  } catch { /* silencieux */ }
};

// ─── Écriture console ─────────────────────────────────────────────────────────

const writeToConsole = (module: string, level: LogLevel, args: unknown[]): void => {
  if (!CONSOLE_LOG_ENABLED) return;
  const prefix = `[${module}] - ${getTime()} -`;
  switch (level) {
    case 'warn'  : originals.warn  (prefix, ...args); break;
    case 'error' : originals.error (prefix, ...args); break;
    case 'info'  : originals.info  (prefix, ...args); break;
    case 'debug' : originals.debug (prefix, ...args); break;
    default      : originals.log   (prefix, ...args); break;
  }
};

// ─── Envoi batch ──────────────────────────────────────────────────────────────

const flushQueue = async (): Promise<void> => {
  if (queue.length === 0) return;

  const batch = [...queue];
  queue    = [];
  dedupMap = new Map(); // reset de la fenêtre de dédup au flush

  for (const entry of batch) {
    await sendRemoteLog(currentModule, entry.level, formatMessage(entry), false);
  }
};

// ─── Flush keepalive avant fermeture d'onglet ─────────────────────────────────

const flushBeforeUnload = (): void => {
  if (queue.length === 0) return;
  const ua  = String(getUserAgentInformations());
  const app = String(getAppInformations());
  for (const entry of queue) {
    fetch(API_URLS.remoteLog, {
      method    : 'POST',
      keepalive : true,
      headers   : {
        'Content-Type'            : 'application/json',
        'Accept'                  : 'application/ld+json',
        'User-Agent-Informations' : ua,
        'App-Informations'        : app,
      },
      body: JSON.stringify({
        message    : `[${entry.level.toUpperCase()}] ${formatMessage(entry)}`,
        filename   : `${currentModule}.txt`,
        connection : false,
      }),
    });
  }
};

// ─── Installation de l'intercepteur global ────────────────────────────────────
//
// Appelé UNE SEULE FOIS dans index.tsx, AVANT root.render().
// Envoie immédiatement un événement INIT enrichi (connection: true).

export const installLogger = (platform: string): void => {
  if (isInstalled) { originals.warn('[Logger] déjà installé'); return; }
  isInstalled = true;

  currentPlatform = platform;
  identity.app    = 'admin';
  currentModule   = composeModule();

  const levels: LogLevel[] = ['log', 'info', 'warn', 'error', 'debug'];
  levels.forEach(level => {
    console[level] = (...args: unknown[]) => {
      if (CONSOLE_LOG_ENABLED) originals[level](...args);
      if (REMOTE_LOG_ENABLED && GLOBAL_REMOTE_LEVELS.includes(level)) {
        const message = serializeArgs(args);
        if (!isIgnored(message)) {
          enqueue(level, message);
        }
      }
    };
  });

  batchTimer = setInterval(flushQueue, BATCH_INTERVAL_MS);

  window.addEventListener('beforeunload', () => {
    if (batchTimer) clearInterval(batchTimer);
    flushBeforeUnload();
  });

  // Événement INIT enrichi : connection: true → Symfony lit IP + UA + AppInfo
  sendRemoteLog(currentModule, 'info', `[INIT] platform=${platform}`, true);

  originals.log(`[Logger] installé — module: ${currentModule} | niveaux: ${GLOBAL_REMOTE_LEVELS.join(', ')} | dédup: ${DEDUP_WINDOW_MS}ms`);
};

// ─── Identité du journal ─────────────────────────────────────────────────────
//
/**
 * Dépose un ou plusieurs morceaux d'identité et renomme le fichier si le nom
 * change. Appelable de partout, avec ce qu'on sait : la racine connaît
 * l'administrateur, la régie sa chaîne, le téléphone son transport.
 *
 * Le renommage flushe d'abord la queue dans l'ANCIEN fichier — une ligne
 * appartient à la session où elle a été écrite, pas à celle qui la trouve.
 */
export const setLoggerIdentity = (parts: Partial<LoggerIdentity>): void => {
  Object.assign(identity, parts);
  const newModule = composeModule();

  // Rien à faire si le nom n'a pas bougé : sans ce garde, un rendu de plus
  // renverrait un événement LOGIN et recouperait la queue pour rien.
  if (newModule === currentModule) return;

  if (queue.length > 0) {
    const ua       = String(getUserAgentInformations());
    const app      = String(getAppInformations());
    const snapshot = [...queue];
    queue    = [];
    dedupMap = new Map();
    for (const entry of snapshot) {
      fetch(API_URLS.remoteLog, {
        method    : 'POST',
        keepalive : true,
        headers   : {
          'Content-Type'            : 'application/json',
          'Accept'                  : 'application/ld+json',
          'User-Agent-Informations' : ua,
          'App-Informations'        : app,
        },
        body: JSON.stringify({
          message    : `[${entry.level.toUpperCase()}] ${formatMessage(entry)}`,
          filename   : `${currentModule}.txt`,
          connection : false,
        }),
      });
    }
  }

  currentModule = newModule;

  // Événement LOGIN enrichi : connection: true → Symfony enregistre IP + UA + AppInfo
  sendRemoteLog(currentModule, 'info', `[LOGIN] ${currentModule}`, true);

  originals.log(`[Logger] identité mise à jour → ${currentModule}`);
};

// ─── Factory createLogger (usage métier ciblé) ───────────────────────────────
//
// Usage :
//   const logger = createLogger('HlsAcquirer');
//   logger.warn('buffer faible', { level: 12 });
//
// Les logs métier passent aussi par la déduplication (même fenêtre globale).

export const createLogger = (module: string) => {

  const write = (level: LogLevel, args: unknown[]): void => {
    writeToConsole(module, level, args);
    if (REMOTE_LOG_ENABLED) {
      const message = `[${module}] ${serializeArgs(args)}`;
      enqueue(level, message);
    }
  };

  return {
    log   : (...args: unknown[]) => write('log',   args),
    info  : (...args: unknown[]) => write('info',  args),
    warn  : (...args: unknown[]) => write('warn',  args),
    error : (...args: unknown[]) => write('error', args),
    debug : (...args: unknown[]) => write('debug', args),

    // connection: true → Symfony enrichit avec IP + UA + AppInfo
    // Pas de déduplication pour les événements de connexion (toujours uniques)
    connection: (...args: unknown[]) => {
      writeToConsole(module, 'info', args);
      if (REMOTE_LOG_ENABLED) {
        sendRemoteLog(currentModule, 'info', `[${module}] [CONNECTION] ${serializeArgs(args)}`, true);
      }
    },
  };
};
// ─── Canal de métriques ───────────────────────────────────────────────────────
//
// POURQUOI UN CANAL À PART. Les relevés de débit partaient jusqu'ici en
// `console.warn`, donc dans le journal général — noyés entre les erreurs, les
// chutes de connexion et le reste. Une mesure ne se lit pas comme un incident :
// elle se lit en série, ligne après ligne, pour voir une courbe. D'où un fichier
// par émetteur : `metrics_regie_<chaîne>.txt` côté régie, `metrics_device_<id>.txt`
// par téléphone. Symfony préfixe déjà chaque nom par l'IP, donc deux téléphones
// derrière deux liens différents ne se marchent jamais dessus.
//
// LES DEUX INTERRUPTEURS. `METRICS_LOG_ENABLED` est celui du développeur, à côté
// de REMOTE_LOG_ENABLED et CONSOLE_LOG_ENABLED : à false, plus une ligne ne part,
// quoi que fasse l'opérateur. `setMetricsEnabled` est celui de l'opérateur, câblé
// au bouton de l'onglet Débits. Le premier autorise, le second décide — et la
// mesure ne part que si les deux sont d'accord.
//
// PAR DÉFAUT ÉTEINT côté opérateur : une régie ouverte toute la journée écrirait
// des milliers de lignes que personne n'ira lire. On l'allume pour une prise.

let metricsChannel = '';
let metricsOn      = false;
let metricsQueue   : string[] = [];
let metricsTimer   : ReturnType<typeof setInterval> | null = null;

// Les mesures sont groupées avant l'envoi : le relevé du device tombe toutes les
// 2 s, et un POST par mesure ferait, sur une prise d'une heure, 1800 requêtes par
// téléphone. Elles partent donc par paquets, séparées par des sauts de ligne —
// `Trace::writeToTraceFile` horodate le paquet, chaque ligne porte déjà son
// propre horodatage.
const METRICS_FLUSH_MS = 10_000;

// Symfony valide le nom de fichier sur `^[\w\-. ]+\.txt$` : tout ce qui sort de
// là fait échouer l'écriture en silence, et on ne s'en aperçoit qu'en cherchant
// un fichier qui n'existe pas.
const sanitizeChannel = (name: string): string =>
  name.replace(/[^\w\-.]/g, '-').slice(0, 80) || 'inconnu';

const flushMetrics = (): void => {
  if (metricsQueue.length === 0 || !metricsChannel) return;
  const batch  = metricsQueue;
  metricsQueue = [];
  // Volontairement hors du logger ordinaire : passer par console.* rebouclerait
  // dans l'intercepteur, et la queue générale est restreinte à warn/error.
  void axios.post(
    API_URLS.remoteLog,
    { message: batch.join('\n'), filename: `metrics_${metricsChannel}.txt`, connection: false },
    {
      headers: {
        'Accept'                  : 'application/ld+json',
        'User-Agent-Informations' : String(getUserAgentInformations()),
        'App-Informations'        : String(getAppInformations()),
      },
    },
  ).catch(() => { /* silencieux : une mesure perdue ne vaut pas une erreur à l'écran */ });
};

/**
 * Nomme le fichier de cet émetteur. À appeler dès qu'on sait qui on est — la
 * chaîne pour la régie, l'identifiant pour un téléphone. Changer de canal vide
 * ce qui restait en attente dans l'ancien : une mesure appartient au canal où
 * elle a été prise, pas à celui où le flush la trouve.
 */
export const setMetricsChannel = (name: string): void => {
  const next = sanitizeChannel(name);
  if (next === metricsChannel) return;
  flushMetrics();
  metricsChannel = next;
};

/** L'interrupteur de l'opérateur (bouton de l'onglet Débits). */
export const setMetricsEnabled = (on: boolean): void => {
  if (on === metricsOn) return;
  metricsOn = on;
  if (on) {
    if (!metricsTimer) metricsTimer = setInterval(flushMetrics, METRICS_FLUSH_MS);
  } else {
    // On vide avant de couper : les dernières mesures sont justement celles qui
    // expliquent pourquoi on coupe.
    flushMetrics();
    if (metricsTimer) { clearInterval(metricsTimer); metricsTimer = null; }
  }
};

export const isMetricsEnabled = (): boolean => METRICS_LOG_ENABLED && metricsOn;

/** Une mesure, une ligne. Sans effet tant que les deux interrupteurs ne sont pas d'accord. */
export const logMetric = (line: string): void => {
  if (!METRICS_LOG_ENABLED || !metricsOn || !metricsChannel) return;
  metricsQueue.push(`${getTime()} ${line}`);
};
